<template>
  <div
    v-show="pickerDisplayed"
    class="emoji-popover-wrap"
  >
    <div
      v-if="pickerDisplayed"
      v-click-outside="closePopover"
      class="emoji-picker-wrap"
      :style="popupStyle"
    >
      <EmojiPicker
        :emoji-index="emojiIndex"
        @addEmoji="emojiSelected"
      />
    </div>
  </div>
</template>

<script>
import EventBus from '@/utilities/eventBus';
import EmojiPicker from './EmojiPicker.vue';
import { EmojiIndex } from 'emoji-mart-vue-fast/src';
import { getElementPositionByPointer } from '@/utilities/events/pointer';
import { ResourceType } from '@/types/resources';

export default {
  name: 'EmojiPopover',

  components: {
    EmojiPicker,
  },

  data() {
    return {
      popupStyle: {},
      resourceId: '',
      parentResourceId: '',
      resourceType: '',
      resourceBelongsToId: '',
      pickerHeight: 420,
      emojiIndex: null,
    };
  },

  computed: {
    userId() {
      return this.$store.getters.getUserId;
    },
    pickerDisplayed() {
      return this.$store.getters.getEmojiPickerDisplayed;
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setEmojiIndex();
    });
    EventBus.$on('displayEmojiPicker', this.displayEmojiPickerHandler);
    EventBus.$on('closePopover', this.closePopover);
  },

  beforeUnmount() {
    EventBus.$off('displayEmojiPicker');
    EventBus.$off('closePopover');
  },
  methods: {
    closePopover(e) {
      this.$store.dispatch('setEmojiPickerDisplayed', '');
      this.resourceId = '';
      this.resourceType = '';
      this.parentResourceId = '';
      this.resourceBelongsToId = '';
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }
    },
    displayEmojiPickerHandler({
      resourceId,
      parentResourceId,
      resourceBelongsToId,
      resourceType,
      event,
    }) {
      // set local variables
      this.resourceId = resourceId;
      this.resourceType = resourceType;
      this.parentResourceId = parentResourceId;
      this.resourceBelongsToId = resourceBelongsToId;
      // get position of popup from pointer event
      this.popupStyle = getElementPositionByPointer(event, this.pickerHeight);
      if (this.isMobile) {
        // if popup is out of screen, move it to the left
        if (parseInt(this.popupStyle.right, 10) + 410 > window.innerWidth) {
          this.popupStyle.right = 'auto';
          this.popupStyle.left = '12px';
        }
      }
      this.$store.dispatch('setEmojiPickerDisplayed', `${resourceType}-${resourceId}`);
    },

    async setEmojiIndex() {
      const jsonModule = await import(
        /* webpackChunkName: "emojisData" */ 'emoji-mart-vue-fast/data/all.json'
      );
      const emojiJson = jsonModule.default;
      const emojiIndex = new EmojiIndex(emojiJson);
      this.emojiIndex = emojiIndex;
      // make emojiIndex available in all components
      StVueApp.config.globalProperties.$emojiIndex = emojiIndex;
    },

    emojiSelected(emoji) {
      switch (this.resourceType) {
        case 'page-comment':
        case ResourceType.CardComment:
          // check if user already reacted to this comment
          let relevantComments;
          if (this.resourceType === ResourceType.CardComment) {
            relevantComments = this.$store.getters.getCardCommentsAndActivities(
              this.resourceBelongsToId
            );
          } else {
            relevantComments = this.$store.getters.getComments;
          }
          const parent = relevantComments.find((c) => c.id === this.parentResourceId);
          const comment = parent
            ? parent.children?.child_comments?.find((c) => c.id === this.resourceId)
            : relevantComments.find((c) => c.id === this.resourceId);
          const reaction = comment?.reactions?.find((r) => r.id === emoji.id);
          const userReacted = reaction?.members.find((member) => member.user_id === this.userId);

          const actionType = userReacted ? 'removeCommentReaction' : 'toggleCommentReaction';

          this.$store.dispatch(actionType, {
            commentId: this.resourceId,
            parentResourceId: this.parentResourceId,
            userId: this.userId,
            reaction: emoji,
            ...(this.resourceType === ResourceType.CardComment && {
              cardId: this.resourceBelongsToId,
            }),
          });
          break;
        case 'page':
        case 'card':
        default:
          break;
      }
      this.closePopover();
    },

    resetEmojiPicker() {
      this.$nextTick(() => {
        if (!this.$refs.emojiPicker) return;
        this.$refs.emojiPicker.onAnchorClick(this.$refs.emojiPicker.categories[0]);
      });
    },
  },
};
</script>

<style lang="scss">
.emoji-popover-wrap {
  z-index: 100;
  @apply absolute
    top-0
    left-0
    w-full
    h-screen;

  .emoji-picker-wrap {
    @apply absolute
      top-0
      left-0
      rounded-lg;

    box-shadow:
      0px 2px 16px -4px rgba(16, 24, 40, 0.32),
      0px -2px 16px -4px rgba(16, 24, 40, 0.1);

    .emoji-mart {
      @apply rounded-lg
        bg-important-bg
        border
        border-solid
        border-important-border;
      font-family: $primaryFontFamily;

      .emoji-mart-search {
        @apply p-0
          mt-0;

        input {
          @apply rounded-t-lg
            rounded-none
            border-0
            border-b
            border-dividers-default
            pb-2
            pt-3
            px-4
            text-xs
            text-text-default
            bg-important-bg;

          &::placeholder {
            @apply text-text-placeholder;
          }
        }
      }

      .emoji-mart-scroll {
        @apply mr-1
          mt-1
          pr-0;
        // Standard version (Firefox only for now)
        scrollbar-color: $gray200 transparent;

        &::-webkit-scrollbar {
          @apply flex
            h-2
            w-1.5;
        }
        &::-webkit-scrollbar-thumb {
          @apply pr-1
            rounded-lg
            bg-scrollBar-default;
        }
        &::-webkit-scrollbar-track {
          background: transparent;
        }

        .emoji-mart-category-label {
          @apply text-xs
            uppercase
            sticky
            backdrop-blur-sm
            text-text-default
            bg-important-bg;

          h3 {
            @apply py-2;
            //px-2.5;
          }
        }
        .emoji-mart-category {
          @apply pl-1 select-none;

          .emoji-mart-emoji {
            @apply cursor-pointer;
            span {
              @apply cursor-pointer;
            }
            &::before {
              @apply bg-important-hover;
            }
          }
        }
      }
    }
  }
}

.mobile {
  .emoji-popover-wrap {
    .emoji-picker-wrap {
      .emoji-mart {
        max-width: calc(100vw - 24px);
      }
    }
  }
}
</style>
